import * as Sentry from '@sentry/react'
import { type BrowserOptions, type ErrorEvent } from '@sentry/react'

/**
 * Sentry設定
 * すべての環境で有効化するが、ローカル(localhost)では明示的にデバッグが有効な場合のみ送信する。
 * 環境はプロジェクト分割せず `environment` とタグで管理する。
 * また「視覚的コンテキスト（Session Replay）」は、本番では無効、staging/development では有効化する。
 */
const isDevelopment = import.meta.env.DEV
const isDebugExplicitlyEnabled = import.meta.env.VITE_SENTRY_DEBUG === '1'
// ローカル(開発サーバー/preview/静的配信であっても hostname が localhost の場合)は明示的に有効化された場合のみ送信
// それ以外（stg/prod 等の配信環境）では常に送信
const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(
  window.location.hostname
)
const isSentryEnabled = isLocalhost ? isDebugExplicitlyEnabled : true

// 現在の環境名（Vite カスタム環境変数優先）
const currentEnv = (
  import.meta.env.VITE_ENV ||
  import.meta.env.MODE ||
  ''
).toLowerCase()

// 本番環境判定。本番は production のみが唯一の想定 (#5060)
const isProductionLike = currentEnv === 'production' || currentEnv === 'prod'

// 視覚的コンテキスト（Session Replay）は本番では無効、それ以外で有効
const visualContextIntegrations = isProductionLike
  ? []
  : [
      Sentry.replayIntegration({
        // 機密情報の露出抑止（必要に応じて追加調整）
        blockAllMedia: true
      })
    ]

/**
 * 動作保証環境の決定 (#3054) のために viewport の分布を実測する。
 *
 * 幅の境界は #3054 の論点に合わせている。
 * 1024 は「1024 x 768 以下は非対応」の候補ライン、1200 は現行コードの最小幅
 * (ContentLayout の min-w-[970px] + Sider 230px)、1280 は #3054 の提案値、
 * 1366 / 1536 / 1920 は実機で頻出する幅。
 *
 * 高さの境界は #3054 の「1366 x 768 を最低動作環境に含めるか
 * (高さ方向でビューポート 700px を下回る可能性あり)」を判定できるように取る。
 * 700 がその判定ラインで、下回る側の分解能を確保するため 600 も置く。
 */
const VIEWPORT_WIDTH_BOUNDARIES = [1024, 1200, 1280, 1366, 1536, 1920]
const VIEWPORT_HEIGHT_BOUNDARIES = [600, 700, 800, 900]

/**
 * Sentry 上でタグ値は文字列としてソートされる。
 * ゼロ詰めしないと分布が幅・高さの順に並ばないため、桁を 4 に揃える。
 */
const padBoundary = (value: number): string => String(value).padStart(4, '0')

const toBucket = (value: number, boundaries: number[]): string => {
  const first = boundaries[0]
  if (value < first) return `${padBoundary(0)}-${padBoundary(first - 1)}`

  for (let i = 0; i < boundaries.length - 1; i++) {
    const lower = boundaries[i]
    const upper = boundaries[i + 1]
    if (value < upper) return `${padBoundary(lower)}-${padBoundary(upper - 1)}`
  }

  return `${padBoundary(boundaries[boundaries.length - 1])}+`
}

export const toViewportBucket = (width: number): string =>
  toBucket(width, VIEWPORT_WIDTH_BOUNDARIES)

export const toViewportHeightBucket = (height: number): string =>
  toBucket(height, VIEWPORT_HEIGHT_BOUNDARIES)

/**
 * devicePixelRatio をタグ値に落とす。
 *
 * そのまま文字列化すると macOS のスケーリング解像度で無限小数になり
 * (例: 1.7647058823529411)、端末ごとに固有値が生まれて集計軸として使えない。
 * 小数第 2 位で丸めて畳む。
 *
 * 本値は HiDPI (Retina 等) と OS の表示倍率とブラウザズームの積であり、
 * これ単体で「表示倍率が 100% か」を判定することはできない。あくまで補助情報として扱う。
 * 例えば Windows で OS 125% とブラウザズーム 125% は区別できず、
 * macOS の Retina は等倍表示でも 2 を返す。読むときは os.name で層別すること。
 * 実際に効く幅・高さの判断には inner_width / inner_height を使う。
 */
export const toPixelRatioTag = (ratio: number): string =>
  String(Math.round(ratio * 100) / 100)

type EventTags = ErrorEvent['tags']

/**
 * window.screen.* は物理解像度ではなく CSS ピクセル幅を返す。
 * OS の拡大率が掛かった後の値なので、devicePixelRatio と併せて記録して
 * 「拡大率 100% を前提にしてよいか」を後から検算できるようにする。
 *
 * 幅・高さとも inner (レイアウトが実際に使える領域) と screen (画面全体) の
 * 両方を採る。ブラウザの UI とタスクバーの分だけ inner は screen を下回り、
 * 表示崩れに効くのは inner の側であるため。
 */
const viewportTags = (): Record<string, string> => ({
  viewport_bucket: toViewportBucket(window.innerWidth),
  viewport_height_bucket: toViewportHeightBucket(window.innerHeight),
  inner_width: String(window.innerWidth),
  inner_height: String(window.innerHeight),
  screen_width: String(window.screen.width),
  screen_height: String(window.screen.height),
  device_pixel_ratio: toPixelRatioTag(window.devicePixelRatio)
})

/**
 * viewport のタグをイベントに載せる。
 *
 * タグが読まれるのはイベント送信の瞬間だけであるため、スコープへ前もって
 * 書き写さず、送信直前に window から直接読む。
 * 書き込み先が beforeSend / beforeSendTransaction の 2 箇所に閉じるので、
 * スコープの分裂による上書き (initialScope が Sentry.setTag を潰す等) が起こりえない。
 * resize が発火しない経路 (OS の表示倍率変更、開発者ツールの開閉) での変化も取りこぼさない。
 *
 * console.error 由来の Logs は beforeSend を通らない別経路のためタグが載らない。
 * #3054 の集計はエラーとトランザクションで行うため実害はない。
 */
/**
 * 現在見ているテナント。ルートの beforeLoad から書き込む (#5134)。
 *
 * Sentry のスコープではなくモジュールの状態として持つ。
 * スコープへ書くと initialScope / isolation / current のどれに載せるかで
 * マージ順の取り違えが起きうるため、viewport と同じく「送信の瞬間に読む」
 * 一系統に揃える。書き込みは setSentryTenantKey だけ、読み出しは
 * beforeSend / beforeSendTransaction だけになる。
 */
let currentTenantKey: string | null = null

/**
 * 現在のテナントを記録する。テナントが確定していない画面 (ログイン前など) では
 * null を渡してタグを外す。テナントを跨いだ遷移でも後勝ちで更新される。
 */
export const setSentryTenantKey = (tenantKey: string | null): void => {
  currentTenantKey = tenantKey
}

/**
 * 現在選択している拠点。テナントと同じく beforeSend の側で読む (#5134)。
 */
let currentDcId: number | null = null

/**
 * 現在の拠点を記録する。拠点が確定していない画面では null を渡す。
 *
 * 0 は selectedDcIdAtom の初期値で「未選択」を表す (atomWithStorage の既定値、
 * localStorage が空のときも Number(null) で 0 になる)。存在しない拠点 ID なので
 * 未確定として扱う。呼び出し側ではなくここで正規化するのは、書き込み経路が
 * 増えても同じ判定が効くようにするため。
 */
export const setSentryDcId = (dcId: number | null): void => {
  currentDcId = Number.isInteger(dcId) && (dcId as number) > 0 ? dcId : null
}

/**
 * テナント識別子のタグ。
 *
 * どのテナントで起きた事象かを Sentry 上で切り分けるために付ける。
 * 「社内か顧客か」の判定はテナントキーから運用側で導く。
 * 社内かどうかの分類は運用の都合で変わるため、コードには焼き付けない (#5134)。
 */
const tenantTags = (): Record<string, string> =>
  currentTenantKey === null ? {} : { tenant_key: currentTenantKey }

type EventContexts = ErrorEvent['contexts']

/**
 * 拠点はタグではなく context に載せる。
 *
 * 検索軸としては不要で、タグに置くと集計軸のカーディナリティを増やすだけになる
 * (レビュー指摘 #5201)。BE 側は X-DC-Id ヘッダから追えるため FE のみで持つ。
 *
 * 未確定でも既存の context はそのまま返す。トランザクションの contexts.trace は
 * 必須項目で、落とすとエンベロープが壊れる。
 */
const applyEventContexts = (contexts: EventContexts): EventContexts =>
  currentDcId === null ? contexts : { ...contexts, dc: { dc_id: currentDcId } }

const applyEventTags = (tags: EventTags): EventTags => ({
  ...tags,
  ...viewportTags(),
  ...tenantTags()
})

export const config: BrowserOptions = {
  // ローカルは明示的に有効化された場合のみ、その他は常に有効化
  enabled: isSentryEnabled,

  // DSN設定（環境変数から取得）
  dsn: import.meta.env.VITE_SENTRY_DSN,

  // 環境名
  environment: import.meta.env.VITE_ENV || import.meta.env.MODE,

  // リリース情報はViteプラグインにより自動注入（import.meta.env.SENTRY_RELEASE を優先）
  release:
    import.meta.env.SENTRY_RELEASE || import.meta.env.VITE_SENTRY_RELEASE,

  // 基本的なインテグレーション
  integrations: [
    // コンソールの error を Logs として送信（ノイズ抑制のため error のみ）
    Sentry.consoleLoggingIntegration({ levels: ['error'] }),
    Sentry.browserTracingIntegration(),
    Sentry.httpContextIntegration(),
    // 視覚的コンテキスト（Session Replay）: 本番以外のみ有効化
    ...visualContextIntegrations
  ],

  // トレース対象のURL
  tracePropagationTargets: [
    'api.prod.tms.logigo.co.jp',
    'api.stg.tms.logigo.co.jp',
    'api.sand.tms.dev.tms.logigo.co.jp',
    'api.dev.tms.logigo.co.jp',
    /localhost:3001/
  ],

  // サンプリングレート（本番環境は10%、その他は100%）
  tracesSampleRate: isProductionLike ? 0.1 : 1.0,

  // エラーサンプリングレート
  sampleRate: 1.0,

  // Logs 機能の有効化（console.error を収集）
  enableLogs: true,

  // Session Replay のサンプリング（本番は完全無効）
  // - 通常セッション録画: 本番以外でのみ 10%
  // - エラー発生時の録画: 本番以外でのみ 100%
  replaysSessionSampleRate: isProductionLike ? 0 : 0.1,
  replaysOnErrorSampleRate: isProductionLike ? 0 : 1.0,

  // ノイズとなるエラーを除外
  ignoreErrors: [
    // ブラウザ拡張機能のエラー
    /extension\//,
    /^chrome:\/\//,
    /^moz-extension:\/\//,
    // ResizeObserverのエラー（既知の問題）
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // ネットワークエラー（ユーザー環境依存）
    'NetworkError',
    'Failed to fetch'
  ],

  // ノイズとなるURL（ブラウザ拡張や内部ページなど）を含むイベントを除外
  denyUrls: [
    /extensions?\//i, // Chrome/Firefox 拡張
    /^chrome:\/\//i,
    /^moz-extension:\/\//i
  ],

  // エラー送信前の処理
  beforeSend(event, hint) {
    // viewport とテナントのタグ、拠点の context は送信の瞬間に読む (#3054 / #5134)
    event.tags = applyEventTags(event.tags)
    event.contexts = applyEventContexts(event.contexts)

    // 開発環境ではコンソールに出力
    if (isDevelopment) {
      console.log('[Sentry] Event:', event)
      console.log('[Sentry] Hint:', hint)
    }

    // HTTP ステータスの取得（タグ or コンテキスト）
    const httpStatusFromTags = (
      event.tags as Record<string, unknown> | undefined
    )?.http_status
    const httpErrorContext = (
      event.contexts as Record<string, unknown> | undefined
    )?.http_error as Record<string, unknown> | undefined
    const httpStatusFromContext =
      typeof httpErrorContext?.status === 'number'
        ? (httpErrorContext.status as number)
        : undefined
    const httpStatus =
      typeof httpStatusFromTags === 'number'
        ? (httpStatusFromTags as number)
        : httpStatusFromContext

    // 401エラーは除外
    if (httpStatus === 401) {
      return null
    }

    // レベル別の送信制御
    const level = event.level
    // debug/info/log は送信しない
    if (level === 'debug' || level === 'info' || level === 'log') {
      return null
    }
    // 4xx は error と warning のどちらでも保持（通知はルール側で制御）
    // ただし warning で 4xx 以外（例: ライブラリ由来の warning）は除外
    if (level === 'warning') {
      if (
        typeof httpStatus !== 'number' ||
        httpStatus < 400 ||
        httpStatus >= 500
      ) {
        return null
      }
    }

    // node_modules 内のフレームのみから成るスタックは除外（ライブラリ内だけの例外）
    // ただし HTTP エラー（4xx/5xx）として明示的にタグ/コンテキストが付与されているイベントは保持する
    const isHttpErrorEvent =
      typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 600
    if (
      event.exception &&
      event.exception.values &&
      event.exception.values.length > 0
    ) {
      const frames = event.exception.values[0]?.stacktrace?.frames
      if (Array.isArray(frames) && frames.length > 0) {
        const hasAppFrame = frames.some((f) => {
          const filename = f.filename ?? ''
          return (
            typeof filename === 'string' && !filename.includes('node_modules')
          )
        })
        if (!hasAppFrame && !isHttpErrorEvent) {
          return null
        }
      }
    }

    return event
  },

  // トランザクションにも同じタグと context を載せる (#3054 / #5134)
  beforeSendTransaction(event) {
    event.tags = applyEventTags(event.tags)
    event.contexts = applyEventContexts(event.contexts)

    return event
  }
}
